/**
 * Sentry integration for the Sensar Player.
 *
 * Captures uncaught errors, unhandled promise rejections, and any
 * `logger.error(...)` calls. Tags every event with:
 *   - bundleVersion / shellVersion (the OTA versions)
 *   - boardId       (once pairing completes)
 *   - platformKind  (tizen / sssp / web)
 *
 * Boot order: call `initSentry()` from `bundle-entry.tsx` BEFORE any
 * other code that might throw. The `bundle-entry` start() flow then
 * calls `setSentryContext({...})` once it knows the board / versions.
 *
 * Free tier: 5 000 errors / month. The signage fleet is small enough
 * that we should not approach that limit unless something is genuinely
 * on fire — in which case we want to know.
 */

import * as Sentry from '@sentry/browser';
import { platform } from '../platform';

const SENTRY_DSN =
  'https://dea57a84ee5ed50a3358d8321a847105@o4511514288717824.ingest.de.sentry.io/4511514292518992';

let initialised = false;

export function initSentry(): void {
  if (initialised) return;
  initialised = true;

  try {
    Sentry.init({
      dsn: SENTRY_DSN,
      // sendDefaultPii captures the device IP automatically — useful
      // for correlating with API access logs.
      sendDefaultPii: true,
      // Tag every event with the platform impl so we can split crash
      // rates between tizen / sssp / web modes.
      initialScope: {
        tags: {
          platform: platform.kind,
        },
      },
      // Keep the SDK lightweight on signage hardware — drop transport
      // queue if it grows unbounded (we already buffer logs in
      // `logger.ts`).
      maxBreadcrumbs: 50,
      // Avoid sampling errors — fleet is small, every crash matters.
      sampleRate: 1.0,
    });
  } catch (err) {
    // Never let Sentry init throw into the boot path. If it fails (e.g.
    // CSP blocks the ingest domain) we still have the local logger
    // pipeline as a backstop.
    // eslint-disable-next-line no-console
    console.warn('[sentry] init failed', err);
    initialised = false;
  }
}

export interface SentryContext {
  bundleVersion?: string;
  shellVersion?: string;
  boardId?: string;
}

/**
 * Set/update tagging context after pairing or version refresh.
 * Idempotent — safe to call on every start().
 */
export function setSentryContext(ctx: SentryContext): void {
  if (!initialised) return;
  try {
    if (ctx.bundleVersion) {
      Sentry.setTag('bundleVersion', ctx.bundleVersion);
      // Tie source maps + Sentry releases to the bundle version. The
      // shell version is separately tagged below.
      Sentry.setTag('release', `sensar-player@${ctx.bundleVersion}`);
    }
    if (ctx.shellVersion) {
      Sentry.setTag('shellVersion', ctx.shellVersion);
    }
    if (ctx.boardId) {
      Sentry.setUser({ id: ctx.boardId });
      Sentry.setTag('boardId', ctx.boardId);
    }
  } catch {
    // ignore — Sentry context updates should never affect playback.
  }
}

/**
 * Manual capture for `logger.error(...)` calls. The logger calls this
 * (via the hook installed in `bundle-entry.tsx`) so a single
 * `logger.error('foo', { ... })` lands BOTH in the API log batch AND in
 * Sentry, without the caller knowing about either backend.
 */
export function captureLoggedError(
  message: string,
  context?: Record<string, unknown>,
): void {
  if (!initialised) return;
  try {
    Sentry.captureMessage(message, {
      level: 'error',
      extra: context,
    });
  } catch {
    // ignore
  }
}

/**
 * Direct exception capture for places that hold a real Error object —
 * preserves the stack trace and allows Sentry to group by fingerprint.
 */
export function captureException(
  err: unknown,
  context?: Record<string, unknown>,
): void {
  if (!initialised) return;
  try {
    Sentry.captureException(err, {
      extra: context,
    });
  } catch {
    // ignore
  }
}
